/* ============================================================
   AUDIO ENGINE — fully synthesized CRT-TV foley (no asset files)
   ------------------------------------------------------------
   One shared AudioContext, lazily created and resumed on the
   first user gesture. Exposes a small library of clunky 90s TV
   sounds: power relay, button clicks, static bursts, the channel
   "thunk", volume ticks, and a continuous detune-able hiss for
   the no-signal channel.

   Ported verbatim from the prototype's audio.js — behavior-identical.
   Exposed as a singleton `TVAudio` to match the original API.
   ============================================================ */

interface HissNode {
  src: AudioBufferSourceNode
  gain: GainNode
  filter: BiquadFilterNode
}

interface ClickOpts {
  freq?: number
  dur?: number
  level?: number
}

let ctx: AudioContext | null = null
let master: GainNode | null = null
let hissNode: HissNode | null = null
let noiseBuffer: AudioBuffer | null = null
let muted = false

function ensure(): AudioContext | null {
  if (ctx) return ctx
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return null
  ctx = new AC()
  master = ctx.createGain()
  master.gain.value = 0.9
  master.connect(ctx.destination)
  // ~2s of white noise we can re-use everywhere.
  const len = Math.floor(ctx.sampleRate * 2)
  noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate)
  const d = noiseBuffer.getChannelData(0)
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
  return ctx
}

function resume() {
  ensure()
  if (ctx && ctx.state === 'suspended') void ctx.resume()
}

function now(): number {
  return ctx!.currentTime
}

function noiseSource(): AudioBufferSourceNode {
  const s = ctx!.createBufferSource()
  s.buffer = noiseBuffer
  s.loop = true
  s.playbackRate.value = 0.85 + Math.random() * 0.3
  return s
}

// Short filtered white-noise burst (the channel-change "shhk").
function staticBurst(dur?: number, level?: number) {
  if (!ensure() || muted) return
  dur = dur || 0.22
  level = level == null ? 0.5 : level
  const t = now()
  const s = noiseSource()
  const bp = ctx!.createBiquadFilter()
  bp.type = 'highpass'
  bp.frequency.value = 1400
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(level, t + 0.01)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
  s.connect(bp)
  bp.connect(g)
  g.connect(master!)
  s.start(t)
  s.stop(t + dur + 0.02)
}

// A dry mechanical click (button press / relay tick).
function click(opts?: ClickOpts) {
  if (!ensure() || muted) return
  opts = opts || {}
  const t = now()
  const freq = opts.freq || 1700
  const dur = opts.dur || 0.035
  // tiny noise transient
  const s = noiseSource()
  const hp = ctx!.createBiquadFilter()
  hp.type = 'bandpass'
  hp.frequency.value = freq
  hp.Q.value = 0.9
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(opts.level || 0.32, t + 0.004)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
  s.connect(hp)
  hp.connect(g)
  g.connect(master!)
  s.start(t)
  s.stop(t + dur + 0.02)
}

// Chunky two-stage power switch + relay.
function powerSwitch(on: boolean) {
  if (!ensure() || muted) return
  resume()
  const t = now()
  // mechanical clack
  click({ freq: 900, dur: 0.05, level: 0.5 })
  // low relay thump
  const osc = ctx!.createOscillator()
  const g = ctx!.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(on ? 70 : 120, t + 0.02)
  osc.frequency.exponentialRampToValueAtTime(on ? 140 : 55, t + 0.16)
  g.gain.setValueAtTime(0.0001, t + 0.02)
  g.gain.exponentialRampToValueAtTime(0.5, t + 0.05)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32)
  osc.connect(g)
  g.connect(master!)
  osc.start(t + 0.02)
  osc.stop(t + 0.36)
  if (on) degauss(t + 0.18)
}

// The "boooiing" of a CRT degaussing coil at power-on.
function degauss(at?: number) {
  if (!ensure() || muted) return
  const t = at || now()
  const osc = ctx!.createOscillator()
  const g = ctx!.createGain()
  osc.type = 'triangle'
  osc.frequency.setValueAtTime(140, t)
  osc.frequency.exponentialRampToValueAtTime(38, t + 0.7)
  // amplitude wobble
  const lfo = ctx!.createOscillator()
  const lfoG = ctx!.createGain()
  lfo.frequency.setValueAtTime(22, t)
  lfo.frequency.exponentialRampToValueAtTime(6, t + 0.7)
  lfoG.gain.value = 0.16
  lfo.connect(lfoG)
  lfoG.connect(g.gain)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(0.22, t + 0.06)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.8)
  osc.connect(g)
  g.connect(master!)
  osc.start(t)
  osc.stop(t + 0.85)
  lfo.start(t)
  lfo.stop(t + 0.85)
}

// Channel change: a click + a static wash + a low thunk.
function channelThunk() {
  if (!ensure() || muted) return
  click({ freq: 1200, dur: 0.04, level: 0.4 })
  staticBurst(0.26, 0.42)
  const t = now()
  const osc = ctx!.createOscillator()
  const g = ctx!.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(95, t)
  osc.frequency.exponentialRampToValueAtTime(48, t + 0.18)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(0.32, t + 0.03)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24)
  osc.connect(g)
  g.connect(master!)
  osc.start(t)
  osc.stop(t + 0.28)
}

// Soft tick for each volume step.
function volTick() {
  click({ freq: 2200, dur: 0.02, level: 0.16 })
}

// A hollow wooden "whack" — the percussive knock when you smack the set.
function whack() {
  if (!ensure() || muted) return
  resume()
  const t = now()
  // sharp noise transient (the slap)
  const s = noiseSource()
  const bp = ctx!.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = 320
  bp.Q.value = 0.7
  const ng = ctx!.createGain()
  ng.gain.setValueAtTime(0.0001, t)
  ng.gain.exponentialRampToValueAtTime(0.6, t + 0.004)
  ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.09)
  s.connect(bp)
  bp.connect(ng)
  ng.connect(master!)
  s.start(t)
  s.stop(t + 0.12)
  // hollow body resonance (the cabinet thunk)
  const osc = ctx!.createOscillator()
  const og = ctx!.createGain()
  osc.type = 'triangle'
  osc.frequency.setValueAtTime(150, t)
  osc.frequency.exponentialRampToValueAtTime(68, t + 0.18)
  og.gain.setValueAtTime(0.0001, t)
  og.gain.exponentialRampToValueAtTime(0.5, t + 0.012)
  og.gain.exponentialRampToValueAtTime(0.0001, t + 0.3)
  osc.connect(og)
  og.connect(master!)
  osc.start(t)
  osc.stop(t + 0.34)
  // a little rattle of the components settling
  staticBurst(0.12, 0.22)
}

// Continuous static hiss for the no-signal channel.
function startHiss(level?: number) {
  if (!ensure() || muted) return
  if (hissNode) return
  const s = noiseSource()
  const bp = ctx!.createBiquadFilter()
  bp.type = 'highpass'
  bp.frequency.value = 2200
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, now())
  g.gain.linearRampToValueAtTime(level == null ? 0.06 : level, now() + 0.25)
  s.connect(bp)
  bp.connect(g)
  g.connect(master!)
  s.start()
  hissNode = { src: s, gain: g, filter: bp }
}

function stopHiss() {
  if (!hissNode) return
  const h = hissNode
  hissNode = null
  try {
    h.gain.gain.cancelScheduledValues(now())
    h.gain.gain.setValueAtTime(h.gain.gain.value, now())
    h.gain.gain.linearRampToValueAtTime(0.0001, now() + 0.18)
    h.src.stop(now() + 0.2)
  } catch {
    /* already stopped */
  }
}

// A low, original dread drone for the HIDEO scene — a sub sine + a detuned fifth, a slow tremolo
// LFO, all through a lowpass. Synthesized (no sample); NOT anyone's score.
interface DreadNode {
  oscs: OscillatorNode[]
  lfo: OscillatorNode
  gain: GainNode
}
let dreadNode: DreadNode | null = null
const DREAD_BASE = 0.16 // gain at level 1.0

function startDread(level = 1) {
  if (!ensure() || muted) return
  if (dreadNode) return
  const t = now()
  const lp = ctx!.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 320
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, DREAD_BASE * level), t + 1.4)
  // slow tremolo on the gain
  const lfo = ctx!.createOscillator()
  lfo.frequency.value = 0.18
  const lfoGain = ctx!.createGain()
  lfoGain.gain.value = DREAD_BASE * level * 0.4
  lfo.connect(lfoGain)
  lfoGain.connect(g.gain)
  lfo.start()
  // sub + detuned fifth
  const freqs = [55, 55 * 1.5, 55 * 1.503]
  const oscs = freqs.map((f, i) => {
    const o = ctx!.createOscillator()
    o.type = i === 0 ? 'sine' : 'triangle'
    o.frequency.value = f
    const og = ctx!.createGain()
    og.gain.value = i === 0 ? 1 : 0.35
    o.connect(og)
    og.connect(lp)
    o.start()
    return o
  })
  lp.connect(g)
  g.connect(master!)
  dreadNode = { oscs, lfo, gain: g }
}

function setDreadLevel(level: number) {
  if (!dreadNode || !ctx) return
  dreadNode.gain.gain.cancelScheduledValues(now())
  dreadNode.gain.gain.setValueAtTime(Math.max(0.0001, dreadNode.gain.gain.value), now())
  dreadNode.gain.gain.linearRampToValueAtTime(Math.max(0.0001, DREAD_BASE * Math.max(0, level)), now() + 0.2)
}

function stopDread() {
  if (!dreadNode) return
  const d = dreadNode
  dreadNode = null
  try {
    d.gain.gain.cancelScheduledValues(now())
    d.gain.gain.setValueAtTime(Math.max(0.0001, d.gain.gain.value), now())
    d.gain.gain.linearRampToValueAtTime(0.0001, now() + 0.5)
    d.oscs.forEach((o) => o.stop(now() + 0.55))
    d.lfo.stop(now() + 0.55)
  } catch {
    /* already stopped */
  }
}

function setMuted(m: boolean) {
  muted = !!m
  if (muted) { stopHiss(); stopDread() }
}

function isReady(): boolean {
  return !!ctx
}

export const TVAudio = {
  resume,
  click,
  powerSwitch,
  staticBurst,
  channelThunk,
  volTick,
  whack,
  degauss,
  startHiss,
  stopHiss,
  startDread,
  setDreadLevel,
  stopDread,
  setMuted,
  isReady,
}

export type TVAudioApi = typeof TVAudio
